import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  ONBOARDING_FINAL_STEP,
  WHATSAPP_TRANSPORT_LABELS,
  type WhatsappTransport,
} from "@school-sales-ai/shared";
import {
  useClients,
  useUpdateClientTransport,
  useDisconnectClientWhatsapp,
  useSetClientActive,
  useSetClientAccountManager,
} from "@/hooks/useClients";
import { usePlatformUsers } from "@/hooks/usePlatformUsers";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { ClientFormModal } from "@/components/ClientFormModal";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  ApiError,
  listMyClientsApi,
  type ClientOnboardingSummary,
  type ManagedClient,
  type SuperAdminClient,
} from "@/lib/api";

const TRANSPORT_OPTIONS: WhatsappTransport[] = [
  "baileys",
  "cloud_api",
  "cloud_api_coexistence",
];

/**
 * Progress through the onboarding wizard, shown next to the name.
 *
 * Deliberately NOT in the Estado column: a client mid-onboarding is inactive by
 * design (`isActive = false` keeps its origins out of CORS and its users out of
 * login), so folding the two together would read as "someone disabled this".
 * They are independent facts.
 */
function OnboardingBadge({
  onboarding,
}: {
  onboarding: ClientOnboardingSummary | null;
}): React.JSX.Element | null {
  if (!onboarding || onboarding.completedAt !== null) return null;
  // Steps 8 (WhatsApp) and 9 (users) sit outside the completion gate.
  const done = onboarding.completedSteps.filter((s) => s <= ONBOARDING_FINAL_STEP).length;
  return (
    <span className="inline-flex rounded-full bg-lime/25 px-2 py-0.5 text-xs font-semibold text-moss">
      Onboarding {done}/{ONBOARDING_FINAL_STEP}
    </span>
  );
}

// The platform-wide clients table with all super-admin controls.
function SuperAdminClientsView(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: clients, isPending, isError } = useClients();
  const { data: platformUsers = [] } = usePlatformUsers();
  const updateTransport = useUpdateClientTransport();
  const disconnectWhatsapp = useDisconnectClientWhatsapp();
  const setActive = useSetClientActive();
  const setAccountManager = useSetClientAccountManager();

  // Only active account managers can own a school.
  const accountManagers = platformUsers.filter(
    (u) => u.role === "account_manager" && u.active,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [formClient, setFormClient] = useState<SuperAdminClient | null>(null);
  const [disableTarget, setDisableTarget] = useState<SuperAdminClient | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<SuperAdminClient | null>(null);

  if (isPending) return <div className="p-6 text-sm text-ink-500">Cargando clientes…</div>;
  if (isError) return <div className="p-6 text-sm text-red-500">Error al cargar clientes.</div>;

  function handleTransportChange(id: string, transport: WhatsappTransport): void {
    updateTransport.mutate(
      { id, transport },
      {
        onSuccess: () => toast.success("Transporte actualizado"),
        onError: (err) => {
          // The select is already disabled for connected clients, so a 409 here means the number
          // came up between the last list fetch and this request. Say what to do about it — the
          // server message is English and written for a log, not for this screen.
          if (err instanceof ApiError && err.status === 409) {
            toast.error(
              "Este cliente tiene WhatsApp conectado. Desconéctalo antes de cambiar el transporte.",
            );
            return;
          }
          toast.error(err instanceof Error ? err.message : "No se pudo cambiar el transporte.");
        },
      },
    );
  }

  function confirmDisconnect(): void {
    if (!disconnectTarget) return;
    disconnectWhatsapp.mutate(disconnectTarget.id, {
      onSuccess: () => {
        toast.success("WhatsApp desconectado");
        setDisconnectTarget(null);
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "No se pudo desconectar WhatsApp."),
    });
  }

  function handleAccountManagerChange(id: string, value: string): void {
    setAccountManager.mutate(
      { id, accountManagerId: value || null },
      {
        onSuccess: () => toast.success("Account Manager actualizado"),
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "No se pudo asignar el Account Manager.",
          ),
      },
    );
  }

  function openEdit(c: SuperAdminClient): void {
    setFormClient(c);
    setFormOpen(true);
  }

  function handleToggleActive(c: SuperAdminClient): void {
    if (c.isActive) {
      setDisableTarget(c); // disabling is disruptive → confirm first
      return;
    }
    setActive.mutate(
      { id: c.id, isActive: true },
      {
        onSuccess: () => toast.success("Cliente habilitado"),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "No se pudo habilitar el cliente."),
      },
    );
  }

  function confirmDisable(): void {
    if (!disableTarget) return;
    setActive.mutate(
      { id: disableTarget.id, isActive: false },
      {
        onSuccess: () => {
          toast.success("Cliente deshabilitado");
          setDisableTarget(null);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "No se pudo deshabilitar el cliente."),
      },
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-900">Clientes</h1>
        {/* Creating a client IS step 1 of the wizard — same fields, one place. */}
        <Button onClick={() => navigate("/super-admin/onboarding/new")}>
          Nuevo cliente
        </Button>
      </div>

      <div className="shrink-0 overflow-x-auto rounded-lg border border-ink-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-ink-050 text-xs font-semibold uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-left">Transporte WhatsApp</th>
              <th className="px-4 py-3 text-left">Account Manager</th>
              <th className="px-4 py-3 text-left">Creado</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {clients.map((c) => (
              <tr key={c.id} className={`hover:bg-ink-050 ${c.isActive ? "" : "opacity-55"}`}>
                <td className="px-4 py-3 font-medium text-ink-900">
                  <div className="flex flex-wrap items-center gap-2">
                    {c.name}
                    <OnboardingBadge onboarding={c.onboarding} />
                  </div>
                </td>
                <td className="px-4 py-3 text-ink-500">{c.slug}</td>
                <td className="px-4 py-3 text-ink-700">{c.plan?.name ?? "Sin plan"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                      c.isActive
                        ? "bg-stage-inscrito-bg text-stage-inscrito-fg"
                        : "bg-ink-100 text-ink-500"
                    }`}
                  >
                    {c.isActive ? "Activo" : "Deshabilitado"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col items-start gap-1">
                    <select
                      value={c.whatsappTransport}
                      // Locked while a number is live: the switch would 409 anyway, and a select
                      // that silently rejects reads as a bug. Disconnect is the way through.
                      disabled={updateTransport.isPending || c.whatsappConnected}
                      title={
                        c.whatsappConnected
                          ? "Desconecta WhatsApp para poder cambiar el transporte"
                          : undefined
                      }
                      onChange={(e) =>
                        handleTransportChange(c.id, e.target.value as WhatsappTransport)
                      }
                      className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm text-ink-900 disabled:opacity-50"
                    >
                      {TRANSPORT_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {WHATSAPP_TRANSPORT_LABELS[t]}
                        </option>
                      ))}
                    </select>
                    {c.whatsappConnected && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 font-medium text-moss">
                          <span className="h-1.5 w-1.5 rounded-full bg-moss" aria-hidden />
                          Conectado
                        </span>
                        <button
                          type="button"
                          onClick={() => setDisconnectTarget(c)}
                          disabled={disconnectWhatsapp.isPending}
                          className="text-ink-500 hover:underline disabled:opacity-50"
                        >
                          Desconectar
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={c.accountManagerId ?? ""}
                    disabled={setAccountManager.isPending}
                    onChange={(e) => handleAccountManagerChange(c.id, e.target.value)}
                    className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm text-ink-900 disabled:opacity-50"
                  >
                    <option value="">— Sin asignar —</option>
                    {accountManagers.map((am) => (
                      <option key={am.id} value={am.id}>
                        {am.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-ink-500">
                  {new Date(c.createdAt).toLocaleDateString("es-MX")}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="text-moss hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(c)}
                      disabled={setActive.isPending}
                      className="text-ink-500 hover:underline disabled:opacity-50"
                    >
                      {c.isActive ? "Deshabilitar" : "Habilitar"}
                    </button>
                    <Link
                      to={`/super-admin/clients/${c.id}/deployment`}
                      className="text-moss hover:underline"
                    >
                      Deployment
                    </Link>
                    {c.onboarding?.completedAt === null && (
                      <Link
                        to={`/super-admin/onboarding/${c.id}`}
                        className="font-medium text-moss hover:underline"
                      >
                        Continuar onboarding
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit only — creation lives in the wizard, which owns the same fields. */}
      {formClient && (
        <ClientFormModal open={formOpen} onOpenChange={setFormOpen} client={formClient} />
      )}

      <AlertDialog
        open={disconnectTarget !== null}
        onOpenChange={(v: boolean) => !v && setDisconnectTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Desconectar WhatsApp de {disconnectTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {disconnectTarget?.whatsappTransport === "baileys"
                ? "Se cerrará la sesión vinculada y se borrarán sus llaves. La escuela dejará de recibir y responder mensajes, y para volver tendrá que escanear un QR nuevo desde su teléfono."
                : "Se olvidarán las credenciales de la API oficial y dejarán de entrar mensajes por ese número. Para volver hay que repetir el alta con Meta."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnectWhatsapp.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              disabled={disconnectWhatsapp.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {disconnectWhatsapp.isPending ? "Desconectando…" : "Desconectar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={disableTarget !== null}
        onOpenChange={(v: boolean) => !v && setDisableTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Deshabilitar {disableTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sus usuarios no podrán iniciar sesión y el asistente dejará de responder
              (web y WhatsApp). Puedes volver a habilitarlo en cualquier momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setActive.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisable}
              disabled={setActive.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {setActive.isPending ? "Deshabilitando…" : "Deshabilitar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// The account manager's scoped Clientes screen: only their assigned schools, plus
// the two things they own end to end — opening a new school and finishing its
// onboarding. No platform controls (transporte, reasignar Account Manager,
// deshabilitar): those belong to the super-admin view above.
// "Gestionar" selects the school (X-Client-Id) and drops them into its pipeline.
function AccountManagerClientsView(): React.JSX.Element {
  const navigate = useNavigate();
  const setActiveClient = useClientSwitcherStore((s) => s.setActiveClient);
  const {
    data: clients = [],
    isPending,
    isError,
  } = useQuery({ queryKey: ["my-clients"], queryFn: listMyClientsApi });

  function manage(c: ManagedClient): void {
    setActiveClient(c.id, c.name);
    navigate("/pipeline");
  }

  if (isPending) return <div className="p-6 text-sm text-ink-500">Cargando escuelas…</div>;
  if (isError)
    return <div className="p-6 text-sm text-red-500">Error al cargar tus escuelas.</div>;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink-900">Clientes</h1>
        {/* Same wizard the super-admin uses — creating the client IS its step 1, and
            the school lands assigned to whoever opened it. */}
        <Button onClick={() => navigate("/super-admin/onboarding/new")}>
          Nuevo cliente
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-lg border border-ink-200 bg-white p-10 text-center text-sm text-ink-500">
          Aún no tienes escuelas. Crea una con “Nuevo cliente” o pide a un
          administrador de plataforma que te asigne una.
        </div>
      ) : (
        <div className="shrink-0 overflow-x-auto rounded-lg border border-ink-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-050 text-xs font-semibold uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {clients.map((c) => (
                <tr key={c.id} className={`hover:bg-ink-050 ${c.isActive ? "" : "opacity-55"}`}>
                  <td className="px-4 py-3 font-medium text-ink-900">
                    <div className="flex flex-wrap items-center gap-2">
                      {c.name}
                      <OnboardingBadge onboarding={c.onboarding} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        c.isActive
                          ? "bg-stage-inscrito-bg text-stage-inscrito-fg"
                          : "bg-ink-100 text-ink-500"
                      }`}
                    >
                      {c.isActive ? "Activo" : "Deshabilitado"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Button onClick={() => manage(c)}>Gestionar</Button>
                      {c.onboarding?.completedAt === null && (
                        <Link
                          to={`/super-admin/onboarding/${c.id}`}
                          className="font-medium text-moss hover:underline"
                        >
                          Continuar onboarding
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// The Clientes screen is role-aware: super-admins get the full platform table;
// account managers get their scoped portfolio of assigned schools.
export function ClientsList(): React.JSX.Element {
  const profile = useCurrentUserProfile();
  if (profile?.role === "account_manager") {
    return <AccountManagerClientsView />;
  }
  return <SuperAdminClientsView />;
}
